import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { IconButton, Stack, Typography } from '@mui/material';
import {
  UnderLineTextStyled,
  UserBlockStyled,
} from './user-form-details.styled';
import { initialUserFormRole, userFormRoleSchema } from './form-config';
import { useEffect, useMemo, useState } from 'react';

import IOption from '@models/option';
import { IconSize } from '@constants';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import { JSelect } from '@atoms/JSelect';
import Jpaper from '@atoms/JPaper';
import { RadioPanelStyled } from '@styled/radio-panel-styled';
import Role from '@authentication/models/role';
import UserFormFooter from '../UserFormFooter';
import UserFormRolesData from '../../models/user-form-roles-data';
import { UserRole } from '../../models/user-role';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useDebouncedCallback } from 'use-debounce';
import { useGetEnterprisesSuggestions } from '@enterprise/queries/useGetEnterprises';
import { useGetEstablishmentsSuggestionsByEnterpriseId } from '@/modules/establishment/queries/useGetEstablishment';
import { useTranslation } from 'react-i18next';
import useUserMutationContext from '../../hooks/useUserMutationContext';
import { yupResolver } from '@hookform/resolvers/yup';

enum selectedTabEnum {
  enterpriseAdmin = 'enterpriseAdmin',
  establishmentAdmin = 'establishmentAdmin',
}
interface UserFormRoleProps {
  includeFooter?: boolean;
  handleChange?: (data: UserFormRolesData, isDirty: boolean) => void;
}
const UserFormRole = ({ includeFooter, handleChange }: UserFormRoleProps) => {
  const { t } = useTranslation();
  const { payload, setIsDirty } = useUserMutationContext();
  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');
  const [enterpriseIdForEstablishment, setEnterpriseIdForEstablishment] =
    useState<string>('');
  const [establishmentInputValue, setEstablishmentInputValue] =
    useState<string>('');
  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );
  const { data: establishments } =
    useGetEstablishmentsSuggestionsByEnterpriseId(
      10,
      1,
      establishmentInputValue,
      enterpriseIdForEstablishment,
      establishmentInputValue.length > 2,
    );
  const [selectedTab, setSelectedTab] = useState<selectedTabEnum | undefined>();
  const methods = useForm<UserFormRolesData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: useMemo(() => initialUserFormRole, [initialUserFormRole]),
    resolver: yupResolver(userFormRoleSchema(t)),
  });
  const { formState, control, getValues, setValue, trigger, watch } = methods;
  const { isValid, isDirty } = formState;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'userRolesEstablishment',
  });
  const setEstablishmentIdAndClearFields = (id: string) => {
    setEnterpriseIdForEstablishment(id);
    remove();
  };
  const debouncedEnterprise = useDebouncedCallback((value) => {
    setEnterpriseInputValue(value);
  }, 500);
  const debouncedEstablishment = useDebouncedCallback((value) => {
    setEstablishmentInputValue(value);
  }, 500);
  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const establishmentsOptions = establishments
    ? establishments.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const setEnterpriseId = (id: string) => {
    setValue('userRolesEnterprise.enterpriseId', id, { shouldDirty: true });
    setValue('userRolesEnterprise.role', Role.ADMIN_ENTERPRISE, {
      shouldDirty: true,
    });
    trigger([
      'userRolesEnterprise.enterpriseId',
      'userRolesEnterprise.role',
      'userRolesEnterprise',
    ]);
  };

  const onClickUserEnterprise = () => {
    setSelectedTab(selectedTabEnum.enterpriseAdmin);
  };

  const onClickUserEstablishment = () => {
    setSelectedTab(selectedTabEnum.establishmentAdmin);
  };

  const isEmptyForm =
    getValues('userRolesEnterprise') === null &&
    getValues('userRolesEstablishment')?.length === 0;
  const roleValues = getValues('userRolesEstablishment')?.map(
    (role) => role.establishmentId,
  );

  const filteredEstablishmentsOptions = establishmentsOptions.filter(
    (option) => !roleValues?.includes(option.value),
  );
  useEffect(() => {
    if (includeFooter) return;

    watch(() => {
      if (handleChange) handleChange(getValues(), isDirty);
    });
  }, [watch, isDirty]);
  useEffect(() => {
    if (payload?.userRolesEnterpriseDto)
      setSelectedTab(selectedTabEnum.enterpriseAdmin);
    if (
      payload?.userRolesEstablishmentDto &&
      payload?.userRolesEstablishmentDto?.length > 0
    )
      setSelectedTab(selectedTabEnum.establishmentAdmin);
  }, [payload, setSelectedTab]);

  useEffect(() => {
    // Check if payload is available and update default values accordingly
    if (payload?.userDto) {
      if (payload.userRolesEnterpriseDto)
        setValue('userRolesEnterprise', payload.userRolesEnterpriseDto);
      if (
        payload.userRolesEstablishmentDto &&
        payload.userRolesEstablishmentDto?.length > 0
      ) {
        setValue('userRolesEstablishment', payload.userRolesEstablishmentDto);
      }

      trigger(['userRolesEnterprise', 'userRolesEstablishment']);
    }
  }, [payload, setValue]);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  return (
    <FormProvider {...methods}>
      <form autoComplete="off" noValidate>
        <Stack gap={2}>
          <UserBlockStyled>
            <Jpaper gap={1} alignItems="stretch" attr={{ fullHeight: true }}>
              <Typography variant="h3" mb={3}>
                {t('ROLES')}
              </Typography>
              <Stack direction={'row'} gap={2} mb={2}>
                <RadioPanelStyled
                  data-qa="admin"
                  onClick={onClickUserEnterprise}
                  selected={selectedTab === 'enterpriseAdmin'}
                >
                  {t('ENTERPRISE')}
                </RadioPanelStyled>
                <RadioPanelStyled
                  data-qa="establishment"
                  onClick={onClickUserEstablishment}
                  selected={selectedTab === 'establishmentAdmin'}
                >
                  {t('ESTABLISHMENT')}
                </RadioPanelStyled>
              </Stack>

              {selectedTab === 'enterpriseAdmin' && (
                <JAutocomplete
                  data-qa="enterprise-admin"
                  {...(payload?.userRolesEnterpriseDto?.enterpriseName
                    ? {
                        defaultValue: {
                          label:
                            payload?.userRolesEnterpriseDto?.enterpriseName ??
                            '',
                          value:
                            payload?.userRolesEnterpriseDto?.enterpriseId ?? '',
                        },
                      }
                    : {})}
                  fullWidth
                  label={t('ENTERPRISE_NAME')}
                  options={enterprisesOptions}
                  onChange={(ev, newValue) =>
                    setEnterpriseId((newValue as IOption)?.value)
                  }
                  onTextChange={(val) => debouncedEnterprise(val)}
                />
              )}

              {selectedTab === 'establishmentAdmin' && (
                <Stack gap={2}>
                  <JAutocomplete
                    data-qa="enterprsie-for-establishment"
                    {...(payload?.userRolesEstablishmentDto &&
                    payload?.userRolesEstablishmentDto?.length > 0
                      ? {
                          defaultValue: {
                            label:
                              payload?.userRolesEstablishmentDto[0]
                                ?.enterpriseName ?? '',
                            value:
                              payload?.userRolesEstablishmentDto[0]
                                ?.enterpriseId ?? '',
                          },
                        }
                      : {})}
                    fullWidth
                    label={t('ENTERPRISE_NAME')}
                    options={enterprisesOptions}
                    onChange={(ev, newValue) =>
                      setEstablishmentIdAndClearFields(
                        (newValue as IOption)?.value,
                      )
                    }
                    onTextChange={(val) => debouncedEnterprise(val)}
                  />

                  {fields.map((item, index) => (
                    <div key={item.enterpriseId}>
                      <Stack direction={'row'} gap={1}>
                        <JAutocomplete
                          data-qa={`establishment-${index}`}
                          {...(payload?.userRolesEstablishmentDto &&
                          payload?.userRolesEstablishmentDto?.length > 0
                            ? {
                                defaultValue: {
                                  label:
                                    payload?.userRolesEstablishmentDto[index]
                                      ?.establishmentName ?? '',
                                  value:
                                    payload?.userRolesEstablishmentDto[index]
                                      ?.establishmentId ?? '',
                                },
                              }
                            : {})}
                          fullWidth
                          label={t('ESTABLISHMENT')}
                          options={filteredEstablishmentsOptions}
                          onChange={(ev, newValue) => {
                            setValue(
                              `userRolesEstablishment[${index}].establishmentId` as `userRolesEstablishment.${number}.establishmentId`,

                              (newValue as IOption)?.value ?? '',
                            );
                            trigger([
                              'userRolesEnterprise',
                              'userRolesEstablishment',
                            ]);
                            trigger(
                              `userRolesEstablishment[${index}].establishmentId` as `userRolesEstablishment.${number}.establishmentId`,
                            );
                          }}
                          onTextChange={(val) => debouncedEstablishment(val)}
                        />

                        <JSelect
                          defaultValue={
                            payload?.userRolesEstablishmentDto &&
                            payload.userRolesEstablishmentDto[index]?.role
                          }
                          fullWidth
                          options={Object.values(UserRole).map((oneRole) => ({
                            label: t(oneRole.toUpperCase()),
                            value: oneRole,
                          }))}
                          size="small"
                          label={t('ROLE')}
                          onChange={(event) => {
                            setValue(
                              `userRolesEstablishment[${index}].role` as `userRolesEstablishment.${number}.role`,

                              event.target.value as string,
                              { shouldDirty: true },
                            );
                            trigger([
                              'userRolesEnterprise',
                              'userRolesEstablishment',
                            ]);
                            trigger(
                              `userRolesEstablishment[${index}].role` as `userRolesEstablishment.${number}.role`,
                            );
                          }}
                        />
                        <IconButton
                          data-qa="error"
                          color="error"
                          disableRipple
                          onClick={() => remove(index)}
                        >
                          <XMarkIcon width={IconSize.buttonIcon} />
                        </IconButton>
                      </Stack>
                    </div>
                  ))}
                  <UnderLineTextStyled
                    data-qa="add-establishment"
                    onClick={() => {
                      setEstablishmentInputValue('');
                      append({
                        establishmentId: '',
                        role: '',
                        enterpriseId: enterpriseIdForEstablishment,
                      });
                    }}
                  >
                    {t('ADD_ESTABLISHMENT')}
                  </UnderLineTextStyled>
                </Stack>
              )}
            </Jpaper>
          </UserBlockStyled>
          {includeFooter && (
            <UserFormFooter
              isValid={isValid && !isEmptyForm}
              values={getValues()}
              submitTab={true}
            />
          )}
        </Stack>
      </form>
    </FormProvider>
  );
};

export default UserFormRole;
